import React, { useRef, useState, useEffect, useCallback } from "react";

import styles from "styles.module.css";
import Filter from "./Filter";
import "./styles.css";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import OverLayLoading from "ui/Loading";
import GridCustomPage from "ui/grid/GridPage";
import { useTranslation } from "react-i18next";
import { GridCellProps } from "@progress/kendo-react-grid";
import { PageTypePath } from "utils/pages";
import { COLOR, ICON_NAMES } from "utils/constant";
import { ContractGridColumn } from "./GridColumns";
import {
	contractConfirmationApi,
	ContractFilter,
	getContractsApi,
} from "api/contract";
import { Contract } from "interface/contract";
import { useInfoStore } from "store/error";
import ContractsMobileList from "./ContractsMobileList";
import CustomModal from "ui/modal/Modal";

import { Loading } from "ui/Loading";
import { usePageAccess } from "hooks/usePageAccess";
import { IconButtonActionType } from "interface";

const pageSize = 60;

const Contracts = () => {
	const { t } = useTranslation();

	const overlayRef = useRef<HTMLDivElement>(null);
	const mobileRequestInProgress = useRef(false);
	// Ref for tracking if there's more data to be loaded
	const mobileDataFinished = useRef(false);
	// Ref for the next skip value
	const mobileNextSkip = useRef(0);

	const gridRef = useRef<HTMLDivElement>(null);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [mobileData, setMobileData] = useState<Contract[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	const [filterOptions, setFilterOptions] = useState<ContractFilter>({
		take: pageSize,
		skip: 0,
		//fromDate: addMonths(new Date(), -36),
		fromDate: new Date(2000, 0, 1),
		toDate: null,
	});

	const [confirmed, setConfirmed] = useState(false);
	// State for pending confirmation prop
	const [pendingConfirmProp, setPendingConfirmProp] = useState<GridCellProps | null>(null);
	// State for pending confirmation item
	const [pendingConfirmItem, setPendingConfirmItem] = useState<Contract | null>(null);

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const { setError } = useInfoStore();

	const { pageAccess } = usePageAccess();

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const openPdf = (prop: GridCellProps) => {
		window.open(
			`/${PageTypePath.Pdf}/${prop.dataItem.reportGuid}`,
			"_blank"
		);
		return;
	};

	const openPdfByItem = (item: Contract) => {
		window.open(`/${PageTypePath.Pdf}/${item.reportGuid}`, "_blank");
	};

	// Initiates contract confirmation process, sets the prop to be confirmed
	const contractConfirmation = (prop: GridCellProps) => {
		setPendingConfirmProp(prop);
	};

	// Handles the confirmation dialog OK action
	const handleConfirmDialogOk = () => {
		// If there's a pending confirmation prop
		if (pendingConfirmProp) {
			contractConfirmationApi({
				contractId: pendingConfirmProp.dataItem.id,
			})
				.then(() => {
					setConfirmed((prev) => !prev);
				})
				.catch((err) => {
					if (err?.response?.data) {
						setError(err?.response?.data.returnText);
					} else {
						setError(err.message);
					}
				})
				.finally(() => {
					setPendingConfirmProp(null);
				});
		// If there's a pending confirmation item
		} else if (pendingConfirmItem) {
			contractConfirmationApi({
				contractId: pendingConfirmItem.id,
			})
				.then(() => {
					setConfirmed((prev) => !prev);
				})
				.catch((err) => {
					if (err?.response?.data) {
						setError(err?.response?.data.returnText);
					} else {
						setError(err.message);
					}
				})
				.finally(() => {
					setPendingConfirmItem(null);
				});
		}
	};

	// Handles the confirmation dialog cancel action
	const handleConfirmDialogCancel = () => {
		setPendingConfirmProp(null);
		setPendingConfirmItem(null);
	};

	const contractConfirmationByItem = (item: Contract): Promise<void> => {
		return new Promise<void>((resolve, reject) => {
			setPendingConfirmItem(item);
			// Resolve/reject is handled via handleConfirmDialogOk/Cancel
			// We store resolvers to call after dialog interaction
			pendingResolveRef.current = resolve;
			pendingRejectRef.current = reject;
		});
	};

	const pendingResolveRef = useRef<(() => void) | null>(null);
	const pendingRejectRef = useRef<((err: unknown) => void) | null>(null);

	// Handles confirmation dialog OK with pending resolve
	const handleConfirmDialogOkWithResolve = () => {
		if (pendingConfirmProp) {
			contractConfirmationApi({
				contractId: pendingConfirmProp.dataItem.id,
			})
				.then(() => {
					setConfirmed((prev) => !prev);
				})
				.catch((err) => {
					if (err?.response?.data) {
						setError(err?.response?.data.returnText);
					} else {
						setError(err.message);
					}
				})
				.finally(() => {
					setPendingConfirmProp(null);
				});
		} else if (pendingConfirmItem) {
			contractConfirmationApi({
				contractId: pendingConfirmItem.id,
			})
				.then(() => {
					setConfirmed((prev) => !prev);
					pendingResolveRef.current?.();
				})
				.catch((err) => {
					if (err?.response?.data) {
						setError(err?.response?.data.returnText);
					} else {
						setError(err.message);
					}
					pendingRejectRef.current?.(err);
				})
				.finally(() => {
					setPendingConfirmItem(null);
					pendingResolveRef.current = null;
					pendingRejectRef.current = null;
				});
		}
	};

	// Handles confirmation dialog cancel with pending resolve
	const handleConfirmDialogCancelWithResolve = () => {
		setPendingConfirmProp(null);
		setPendingConfirmItem(null);
		pendingResolveRef.current?.();
		pendingResolveRef.current = null;
		pendingRejectRef.current = null;
	};

	// Requests mobile data for contracts with pagination
	const requestMobileData = useCallback((skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getContractsApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = (response?.data || []) as Contract[];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setMobileData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setMobileData(incoming);
				} else {
					setMobileData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			});
	}, [filterOptions]);

	// Effect for updating mobile view state on window resize
	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	// Effect for fetching mobile data on mount or when confirmed state changes
	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [confirmed, isMobileView, requestMobileData]);

	if (!pageAccess) {
		return <Loading show={true} />;
	}

	// Determine if the confirmation dialog should be shown
	const showConfirmDialog = pendingConfirmProp !== null || pendingConfirmItem !== null;

	return (
		<div className={`${styles.PageContainer} contract-page`}>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>{t("SalesContract")}</legend>
			</div>

			{/* Confirmation dialog for contract actions */}
			{showConfirmDialog && (
				<CustomModal
					title={t("Confirm")}
					onClose={handleConfirmDialogCancelWithResolve}
					actions={[
						{
							label: t("Cancel"),
							onClick: handleConfirmDialogCancelWithResolve,
							bgColor: COLOR.Green,
							color: "white",
						},
						{
							label: t("Ok"),
							onClick: handleConfirmDialogOkWithResolve,
							bgColor: COLOR.Green,
							color: "white",
						},
					]}
				>
					<p style={{ padding: "16px 0" }}>
						Bekreftelse av denne kontrakten regnes samtidig som salgsbekrefelse for dette partiet
					</p>
				</CustomModal>
			)}

			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{isMobileView ? (
					<ContractsMobileList
						items={mobileData}
						loading={mobileLoading}
						onConfirm={contractConfirmationByItem}
						onOpenPdf={openPdfByItem}
						onLoadMore={() => requestMobileData(mobileNextSkip.current, pageSize)}
					/>
				) : (
					<GridCustomPage
						dataKey="id"
						gridColumns={ContractGridColumn()}
						filter={filterOptions}
						forceUpdate={confirmed}
						exportGridAsPDFRef={pdfRef}
						lastColWidth="140px"
						lastColBtns={[
							{
								iconName: ICON_NAMES.Confirmation,
								showConditionValue: 1,
								conditionField: "showSign",
								onclick: contractConfirmation,
								actionType: IconButtonActionType.Update,
								title: t("ConfirmContract"),
							},
							{
								iconName: ICON_NAMES.Download,
								alwaysShow: true,
								onclick: openPdf,
								title: t("ViewContract"),
							},
						]}
						pageAccess={pageAccess}
						exportGridAsExcelRef={excelRef}
						apiMethod={getContractsApi}
						showDetailIcon={false}
					/>
				)}
			</div>
		</div>
	);
};

export default Contracts;
